import type { FormEvent, ReactNode } from "react";
import { ContentPasteGo } from "@mui/icons-material";
import { CircularProgress } from "@mui/material";
import Button from "@components/Button/Button";
import type { FileUploadConditions } from "@components/FileUpload/FileUpload.types";
import SingleFile from "@components/FileUpload/SingleFile";
import type { UploadEntry } from "@components/FileUpload/uploadStore";
import FormItem from "@components/FormItem/FormItem";
import FormSection from "@components/FormSection/FormSection";
import GenericDialog from "@components/GenericDialog/GenericDialog";
import Subheader from "@components/Subheader/Subheader";
import { redirectForTranslateFromEnglishToArabic } from "@utils/translateFromEnglishToArabic";
import { FormProvider, type UseFormReturn } from "react-hook-form";
import { VENDOR_LICENSE_REQUEST_FIELDS as F } from "./constants";
import type { VendorLicenseRequestFormData } from "./schema/vendorLicenseRequestFormSchema";

type Option = {
  value: string;
  label: string;
};

type ExternalDocumentUploadState = {
  uploadStoreKey?: string;
  uploadEntries: UploadEntry[];
  setUploadEntries: (entries: UploadEntry[]) => void;
  handleUpload: (files: File[]) => void;
  handleRemove: () => void;
  notSuccess: boolean;
};

type CancelDialogProps = {
  testId: string;
  title: string;
  cancelButtonText: string;
  confirmButtonText: string;
  handleCloseDialog: () => void;
  confirmAction: () => void;
  elementContent: ReactNode;
};

type VendorLicenseRequestFormTexts = {
  title: string;
  requiredInformation: string;
  attachmentsIntro: string;
  tradeLicenseDocTitle: string;
  companyIncorporationDocTitle: string;
  companyName: string;
  companyNameArabic: string;
  companyType: string;
  natureOfBusiness: string;
  companyEmail: string;
  companyAddress: string;
  applicantName: string;
  applicantPosition: string;
  applicantEmail: string;
  telephoneNumber: string;
  mobileNumber: string;
  faxNumber: string;
  tradingLicenseNumber: string;
  tradingLicenseValidityDate: string;
  tradeLicenseIssuingAuthority: string;
  numberOfEmployees: string;
  numberOfVehicles: string;
  purposeOfRequest: string;
  cancel: string;
  submit: string;
  translate: string;
};

interface VendorLicenseRequestFormContentProps {
  dir: string;
  form: UseFormReturn<VendorLicenseRequestFormData>;
  testIdBase: string;
  texts: VendorLicenseRequestFormTexts;
  companyTypeOptions: Option[];
  tradeLicenseIssuingAuthorityOptions: Option[];
  supportedDocumentExtensions: string[];
  documentConditions: FileUploadConditions;
  uploadDescription: string;
  tradeLicenseUpload: ExternalDocumentUploadState;
  companyIncorpUpload: ExternalDocumentUploadState;
  submitDisabled: boolean;
  onCancelClick: () => void;
  onFormSubmit: (event: FormEvent<HTMLFormElement>) => void;
  cancelDialogOpen: boolean;
  cancelDialogProps: CancelDialogProps;
  headerContent?: ReactNode;
  readOnlyApplicantEmail?: boolean;
  disableNativeNumberPattern?: boolean;
  isTradeLicenseRequired?: boolean;
}

const VendorLicenseRequestFormContent = ({
  dir,
  form,
  testIdBase,
  texts,
  companyTypeOptions,
  tradeLicenseIssuingAuthorityOptions,
  supportedDocumentExtensions,
  documentConditions,
  uploadDescription,
  tradeLicenseUpload,
  companyIncorpUpload,
  submitDisabled,
  onCancelClick,
  onFormSubmit,
  cancelDialogOpen,
  cancelDialogProps,
  headerContent,
  readOnlyApplicantEmail = false,
  disableNativeNumberPattern = false,
  isTradeLicenseRequired = true,
}: VendorLicenseRequestFormContentProps) => {
  return (
    <div dir={dir} className="min-h-screen bg-white flex flex-col">
      {headerContent}

      <Subheader testId={`${testIdBase}-subheader`} title={texts.title} />

      <div className="px-24 pt-8 pb-8 flex-1 flex flex-col">
        <FormProvider {...form}>
          <form onSubmit={onFormSubmit} className="flex-1 flex flex-col">
            <div className="w-full max-w-[980px] mx-auto flex flex-col gap-6">
              <FormSection title={texts.requiredInformation}>
                <FormItem<VendorLicenseRequestFormData, typeof F.COMPANY_NAME>
                  required
                  name={F.COMPANY_NAME}
                  type="text"
                  maxChars={150}
                  label={texts.companyName}
                  testId={`${testIdBase}-companyName`}
                />
                <div />
                <FormItem<VendorLicenseRequestFormData, typeof F.COMPANY_NAME_ARABIC>
                  dir="rtl"
                  required
                  name={F.COMPANY_NAME_ARABIC}
                  type="text"
                  maxChars={150}
                  label={texts.companyNameArabic}
                  testId={`${testIdBase}-companyNameArabic`}
                  actionInLabel
                  textForActionInLabel={texts.translate}
                  onActionClicked={() =>
                    redirectForTranslateFromEnglishToArabic(form.getValues("companyName"))
                  }
                />
                <FormItem<VendorLicenseRequestFormData, typeof F.COMPANY_TYPES>
                  required
                  name={F.COMPANY_TYPES}
                  type="multiple-select"
                  options={companyTypeOptions}
                  label={texts.companyType}
                  testId={`${testIdBase}-companyType`}
                />
                <FormItem<VendorLicenseRequestFormData, typeof F.NATURE_OF_BUSINESS_IN_POF>
                  required
                  name={F.NATURE_OF_BUSINESS_IN_POF}
                  type="text"
                  maxChars={150}
                  label={texts.natureOfBusiness}
                  testId={`${testIdBase}-natureOfBusinessInPof`}
                />
                <FormItem<VendorLicenseRequestFormData, typeof F.COMPANY_EMAIL>
                  required
                  name={F.COMPANY_EMAIL}
                  type="text"
                  maxChars={150}
                  label={texts.companyEmail}
                  testId={`${testIdBase}-companyEmail`}
                />
                <div className="md:col-span-full">
                  <FormItem<VendorLicenseRequestFormData, typeof F.COMPANY_ADDRESS>
                    required
                    name={F.COMPANY_ADDRESS}
                    type="text"
                    maxChars={250}
                    label={texts.companyAddress}
                    testId={`${testIdBase}-companyAddress`}
                  />
                </div>
                <FormItem<VendorLicenseRequestFormData, typeof F.APPLICANT_NAME>
                  required
                  name={F.APPLICANT_NAME}
                  type="text"
                  maxChars={150}
                  label={texts.applicantName}
                  testId={`${testIdBase}-applicantName`}
                />
                <FormItem<VendorLicenseRequestFormData, typeof F.APPLICANT_POSITION>
                  required
                  name={F.APPLICANT_POSITION}
                  type="text"
                  maxChars={100}
                  label={texts.applicantPosition}
                  testId={`${testIdBase}-applicantPosition`}
                />
                <FormItem<VendorLicenseRequestFormData, typeof F.APPLICANT_EMAIL>
                  required
                  name={F.APPLICANT_EMAIL}
                  type="text"
                  maxChars={150}
                  label={texts.applicantEmail}
                  testId={`${testIdBase}-applicantEmail`}
                  disabled={readOnlyApplicantEmail}
                />
                <FormItem<VendorLicenseRequestFormData, typeof F.TELEPHONE_NUMBER>
                  required
                  name={F.TELEPHONE_NUMBER}
                  type="text"
                  maxChars={30}
                  label={texts.telephoneNumber}
                  testId={`${testIdBase}-telephoneNumber`}
                />
                <FormItem<VendorLicenseRequestFormData, typeof F.MOBILE_NUMBER>
                  required
                  name={F.MOBILE_NUMBER}
                  type="text"
                  maxChars={30}
                  label={texts.mobileNumber}
                  testId={`${testIdBase}-mobileNumber`}
                />
                <FormItem<VendorLicenseRequestFormData, typeof F.FAX_NUMBER>
                  required
                  name={F.FAX_NUMBER}
                  type="text"
                  label={texts.faxNumber}
                  testId={`${testIdBase}-faxNumber`}
                />
                <FormItem<VendorLicenseRequestFormData, typeof F.TRADING_LICENSE_NUMBER>
                  required={isTradeLicenseRequired}
                  name={F.TRADING_LICENSE_NUMBER}
                  type="number"
                  maxDigits={10}
                  canBeginWithZero
                  disableNativeNumberPattern={disableNativeNumberPattern}
                  label={texts.tradingLicenseNumber}
                  testId={`${testIdBase}-tradingLicenseNumber`}
                />
                <FormItem<VendorLicenseRequestFormData, typeof F.TRADING_LICENSE_VALIDITY_DATE>
                  required={isTradeLicenseRequired}
                  name={F.TRADING_LICENSE_VALIDITY_DATE}
                  type="date-picker"
                  label={texts.tradingLicenseValidityDate}
                  timePicker={false}
                  testId={`${testIdBase}-tradingLicenseValidityDate`}
                  dateConstraint="not-past"
                />
                <FormItem<VendorLicenseRequestFormData, typeof F.TRADE_LICENSE_ISSUING_AUTHORITY>
                  required={isTradeLicenseRequired}
                  name={F.TRADE_LICENSE_ISSUING_AUTHORITY}
                  type="autocomplete"
                  options={tradeLicenseIssuingAuthorityOptions}
                  label={texts.tradeLicenseIssuingAuthority}
                  testId={`${testIdBase}-tradeLicenseIssuingAuthority`}
                />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-[inherit]">
                  <FormItem<VendorLicenseRequestFormData, typeof F.NUMBER_OF_EMPLOYEES>
                    required
                    name={F.NUMBER_OF_EMPLOYEES}
                    type="number"
                    maxDigits={3}
                    canBeginWithZero
                    disableNativeNumberPattern={disableNativeNumberPattern}
                    label={texts.numberOfEmployees}
                    testId={`${testIdBase}-numberOfEmployees`}
                  />
                  <FormItem<VendorLicenseRequestFormData, typeof F.NUMBER_OF_VEHICLES>
                    required
                    name={F.NUMBER_OF_VEHICLES}
                    type="number"
                    maxDigits={3}
                    canBeginWithZero
                    disableNativeNumberPattern={disableNativeNumberPattern}
                    label={texts.numberOfVehicles}
                    testId={`${testIdBase}-numberOfVehicles`}
                  />
                </div>
                <FormItem<VendorLicenseRequestFormData, typeof F.PURPOSE_OF_REQUEST>
                  required
                  name={F.PURPOSE_OF_REQUEST}
                  type="text"
                  maxChars={200}
                  label={texts.purposeOfRequest}
                  testId={`${testIdBase}-purposeOfRequest`}
                />
              </FormSection>

              <div className="text-sm text-gray-500">{texts.attachmentsIntro}</div>

              <FormSection title={texts.tradeLicenseDocTitle}>
                <div className="md:col-span-full">
                  <FormItem<VendorLicenseRequestFormData, typeof F.TRADE_LICENSE_DOCUMENT_URL>
                    required={isTradeLicenseRequired}
                    name={F.TRADE_LICENSE_DOCUMENT_URL}
                    type="file"
                    maxFiles={1}
                    testId={`${testIdBase}-tradeLicenseUpload`}
                    allowedFileExtensions={supportedDocumentExtensions}
                    uploadConditions={documentConditions}
                    uploadDescription={uploadDescription}
                    uploadStoreKey={tradeLicenseUpload.uploadStoreKey}
                    files={tradeLicenseUpload.uploadEntries}
                    setFiles={(files) =>
                      tradeLicenseUpload.setUploadEntries((files ?? []) as UploadEntry[])
                    }
                    onUpload={(_, files) =>
                      tradeLicenseUpload.handleUpload((files ?? []) as File[])
                    }
                    onRemoveFile={() => tradeLicenseUpload.handleRemove()}
                    notSuccess={tradeLicenseUpload.notSuccess}
                  />
                </div>

                {tradeLicenseUpload.uploadEntries.length > 0 && (
                  <div className="md:col-span-full">
                    <SingleFile
                      testId={`${testIdBase}-tradeLicense-file`}
                      file={tradeLicenseUpload.uploadEntries[0]}
                      fieldName={F.TRADE_LICENSE_DOCUMENT_URL}
                      setFiles={() => tradeLicenseUpload.setUploadEntries([])}
                      removeFn={() => tradeLicenseUpload.handleRemove()}
                      isLoading={tradeLicenseUpload.uploadEntries[0] instanceof File}
                    />
                  </div>
                )}
              </FormSection>

              <FormSection title={texts.companyIncorporationDocTitle}>
                <div className="md:col-span-full">
                  <FormItem<
                    VendorLicenseRequestFormData,
                    typeof F.COMPANY_INCORPORATION_DOCUMENT_URL
                  >
                    required
                    name={F.COMPANY_INCORPORATION_DOCUMENT_URL}
                    type="file"
                    maxFiles={1}
                    testId={`${testIdBase}-companyIncorpUpload`}
                    allowedFileExtensions={supportedDocumentExtensions}
                    uploadConditions={documentConditions}
                    uploadDescription={uploadDescription}
                    uploadStoreKey={companyIncorpUpload.uploadStoreKey}
                    files={companyIncorpUpload.uploadEntries}
                    setFiles={(files) =>
                      companyIncorpUpload.setUploadEntries((files ?? []) as UploadEntry[])
                    }
                    onUpload={(_, files) =>
                      companyIncorpUpload.handleUpload((files ?? []) as File[])
                    }
                    onRemoveFile={() => companyIncorpUpload.handleRemove()}
                    notSuccess={companyIncorpUpload.notSuccess}
                  />
                </div>

                {companyIncorpUpload.uploadEntries.length > 0 && (
                  <div className="md:col-span-full">
                    <SingleFile
                      testId={`${testIdBase}-companyIncorp-file`}
                      file={companyIncorpUpload.uploadEntries[0]}
                      fieldName={F.COMPANY_INCORPORATION_DOCUMENT_URL}
                      setFiles={() => companyIncorpUpload.setUploadEntries([])}
                      removeFn={() => companyIncorpUpload.handleRemove()}
                      isLoading={companyIncorpUpload.uploadEntries[0] instanceof File}
                    />
                  </div>
                )}
              </FormSection>
            </div>

            <div className="w-full max-w-[980px] mx-auto flex items-center justify-between pt-8">
              <Button
                testId={`${testIdBase}-cancel`}
                label={texts.cancel}
                variant="outlined"
                sx={{ textTransform: "none" }}
                type="button"
                disabled={submitDisabled}
                onClick={onCancelClick}
              />
              <Button
                testId={`${testIdBase}-submit`}
                label={texts.submit}
                variant="contained"
                sx={{ textTransform: "none" }}
                type="submit"
                startIcon={
                  submitDisabled ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <ContentPasteGo />
                  )
                }
                disabled={submitDisabled}
              />
            </div>
          </form>
        </FormProvider>
      </div>

      <GenericDialog open={cancelDialogOpen} {...cancelDialogProps} />
    </div>
  );
};

export default VendorLicenseRequestFormContent;
